/**
 * Dynamic Fields Utility
 * Handle dynamic field visibility and value transformations
 */
import { FormFieldConfig } from '../types'

/**
 * Check if a field should be visible based on its visibility condition
 * @param field - The field configuration
 * @param values - Current form values
 * @returns boolean indicating if field should be visible
 */
export const isFieldVisible = (
  field: FormFieldConfig,
  values: Record<string, any>
): boolean => {
  // If no visibility condition, field is always visible
  if (!field.visible) return true

  // Evaluate visibility condition with current values
  try {
    return field.visible(values)
  } catch (error) {
    console.error(`Error evaluating visibility for field ${field.name}:`, error)
    return true // Default to visible on error
  }
}

/**
 * Transform a field value based on its transform function
 * @param field - The field configuration
 * @param value - Current field value
 * @param values - All form values
 * @returns Transformed value
 */
export const transformFieldValue = (
  field: FormFieldConfig,
  value: any,
  values: Record<string, any>
): any => {
  // If no transform function, return original value
  if (!field.transform) return value

  // Apply transform function
  try {
    return field.transform(value, values)
  } catch (error) {
    console.error(`Error transforming value for field ${field.name}:`, error)
    return value // Return original value on error
  }
}

/**
 * Get fields that depend on a specific field
 * @param fieldName - The name of the field
 * @param fields - All form fields
 * @returns Array of dependent fields
 */
export const getDependentFields = (
  fieldName: string,
  fields: FormFieldConfig[]
): FormFieldConfig[] => {
  return fields.filter(field => field.dependencies?.includes(fieldName))
}

/**
 * Check if a field has dependencies
 * @param field - The field configuration
 * @returns boolean indicating if field has dependencies
 */
export const hasDependencies = (field: FormFieldConfig): boolean => {
  return !!field.dependencies && field.dependencies.length > 0
}

/**
 * Get all fields that need to be watched for dynamic behavior
 * @param fields - All form fields
 * @returns Array of field names to watch
 */
export const getFieldsToWatch = (fields: FormFieldConfig[]): string[] => {
  const fieldsToWatch = new Set<string>()

  fields.forEach(field => {
    // Add dependencies
    if (field.dependencies) {
      field.dependencies.forEach(dep => fieldsToWatch.add(dep))
    }

    // Add fields with transform or visibility functions
    if (field.transform || field.visible) {
      fieldsToWatch.add(field.name)
    }
  })

  return Array.from(fieldsToWatch)
}
